/**
 * Registered UTM parameters that should be sent as top-level fields
 */
const REGISTERED_PARAMS = ['utm_source', 'utm_campaign', 'utm_term', 'lar'];

/**
 * Extracts URL parameters and separates them into registered and dynamic parameters
 * @param {URLSearchParams|string} searchParams - URLSearchParams object or search string
 * @returns {Object} - Object containing registeredParams and dynamicParameters
 */
export const extractUrlParameters = (searchParams) => {
  // Convert string to URLSearchParams if needed
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams) 
    : searchParams;

  const registeredParams = {};
  const dynamicParameters = [];

  // Iterate through all parameters
  for (const [key, value] of params.entries()) {
    if (REGISTERED_PARAMS.includes(key)) {
      // Add to registered params
      registeredParams[key] = value;
    } else {
      // Add to dynamic parameters array
      dynamicParameters.push({
        key: key,
        value: value
      });
    }
  }

  return {
    registeredParams,
    dynamicParameters
  };
};

/**
 * Preserves all URL parameters (both registered and dynamic) from the current URL
 * @param {URLSearchParams|string} searchParams - URLSearchParams object or search string
 * @returns {URLSearchParams} - URLSearchParams object containing all parameters
 */
export const preserveAllUrlParams = (searchParams) => {
  // Convert string to URLSearchParams if needed
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams) 
    : searchParams;

  // Create a new URLSearchParams object with all parameters
  const preservedParams = new URLSearchParams();
  
  // Copy all parameters to the new object
  for (const [key, value] of params.entries()) {
    preservedParams.set(key, value);
  }

  return preservedParams;
};

/**
 * Clears all parameter-related localStorage items and re-stores them from URL
 * This is used when creating a new session to ensure parameters match the current URL
 * @param {URLSearchParams|string} searchParams - URLSearchParams object or search string
 * @returns {Object} - Object containing registeredParams and dynamicParameters
 */
export const clearAndRestoreParamsFromUrl = (searchParams) => {
  // Clear all parameter-related localStorage items
  localStorage.removeItem("lar");
  localStorage.removeItem("utm_source");
  localStorage.removeItem("utm_term");
  localStorage.removeItem("utm_campaign");
  localStorage.removeItem("utm_type");
  localStorage.removeItem("dynamic_parameters");
  
  // Extract parameters from URL
  const { registeredParams, dynamicParameters } = extractUrlParameters(searchParams);
  
  // Store registered parameters in localStorage
  if (registeredParams.lar) {
    localStorage.setItem("lar", registeredParams.lar);
  }
  if (registeredParams.utm_source) {
    localStorage.setItem("utm_source", registeredParams.utm_source);
  }
  if (registeredParams.utm_term) {
    localStorage.setItem("utm_term", registeredParams.utm_term);
  }
  if (registeredParams.utm_campaign) {
    localStorage.setItem("utm_campaign", registeredParams.utm_campaign);
  }
  
  // Handle utm_type (not in REGISTERED_PARAMS but should be stored)
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams) 
    : searchParams;
  const utmType = params.get("utm_type");
  if (utmType) {
    localStorage.setItem("utm_type", utmType);
  }
  
  // Store dynamic parameters in localStorage as JSON
  if (dynamicParameters && dynamicParameters.length > 0) {
    localStorage.setItem("dynamic_parameters", JSON.stringify(dynamicParameters));
  }
  
  return {
    registeredParams,
    dynamicParameters
  };
};

/**
 * Restores dynamic parameters from localStorage and adds them to URLSearchParams
 * @param {URLSearchParams} searchParams - URLSearchParams object to add dynamic parameters to
 * @returns {URLSearchParams} - URLSearchParams object with dynamic parameters added
 */
export const restoreDynamicParamsFromStorage = (searchParams) => {
  const dynamicParamsStr = localStorage.getItem("dynamic_parameters");
  if (dynamicParamsStr) {
    try {
      const dynamicParameters = JSON.parse(dynamicParamsStr);
      if (Array.isArray(dynamicParameters)) {
        dynamicParameters.forEach((param) => {
          if (param.key && param.value) {
            // Only add if not already present in URL
            if (!searchParams.has(param.key)) {
              searchParams.set(param.key, param.value);
            }
          }
        });
      }
    } catch (error) {
      console.error("Error parsing dynamic parameters from localStorage:", error);
    }
  }
  return searchParams;
};

